// VendorScorecard — Tedarikçi performans karnesi (anlık hesap).
// Kullanım: <VendorScorecard accountId={id} months={12} />
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { fmtAmount } from '../utils/numberFormat';

const RATING_CFG = {
  A: { color: '#10b981', bg: 'rgba(16,185,129,0.12)', label: 'Mükemmel' },
  B: { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', label: 'İyi' },
  C: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Vasat' },
  D: { color: '#dc2626', bg: 'rgba(220,38,38,0.12)', label: 'Zayıf' },
};

export default function VendorScorecard({ accountId, months = 12 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(months);

  useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    api.get('/purchase-insights/vendor-scorecard/' + accountId, { params: { months: period } })
      .then(r => { if (r.data.success) setData(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [accountId, period]);

  if (loading) return <div style={{ padding: 20, color: 'var(--text-tertiary)' }}>Yükleniyor...</div>;
  if (!data) return null;

  if (!data.total_orders) {
    return (
      <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
        Bu tedarikçi için henüz onaylanmış alış faturası yok.
      </div>
    );
  }

  const rt = data.rating ? RATING_CFG[data.rating] : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Dönem seçici */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
          Son <strong style={{ color: 'var(--text-primary)' }}>{period}</strong> ayın performans verisi
        </div>
        <select value={period} onChange={e => setPeriod(parseInt(e.target.value))} style={{ padding: '4px 10px', fontSize: 'var(--text-sm)' }}>
          <option value={3}>3 ay</option>
          <option value={6}>6 ay</option>
          <option value={12}>12 ay</option>
          <option value={24}>24 ay</option>
          <option value={36}>36 ay</option>
        </select>
      </div>

      {/* Genel Skor */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14 }}>
        {/* Overall card */}
        <div style={{ ...S.bigCard, ...(rt ? { background: rt.bg, borderColor: rt.color } : {}) }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em' }}>
            Genel Skor
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 6 }}>
            <div style={{ fontSize: 48, fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: rt?.color || 'var(--text-primary)' }}>
              {data.overall_score ?? '—'}
            </div>
            {data.rating && (
              <div>
                <div style={{ fontSize: 32, fontWeight: 800, color: rt.color }}>{data.rating}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: rt.color }}>{rt.label}</div>
              </div>
            )}
          </div>
          <div style={{ marginTop: 8, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
            Zamanında teslim %60 + fiyat istikrarı %40
          </div>
        </div>

        {/* Sağ taraf KPI'lar */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={S.miniCard}>
            <div style={S.miniLabel}>Toplam Sipariş</div>
            <div style={S.miniValue}>{data.total_orders}</div>
          </div>
          <div style={S.miniCard}>
            <div style={S.miniLabel}>Toplam Tutar</div>
            <div style={{ ...S.miniValue, color: 'var(--accent-primary)', fontSize: 'var(--text-lg)' }}>{fmtAmount(data.total_amount)} ₺</div>
          </div>
          <div style={S.miniCard}>
            <div style={S.miniLabel}>Zamanında %</div>
            <div style={{ ...S.miniValue, color: data.on_time_pct >= 80 ? 'var(--success)' : data.on_time_pct >= 60 ? 'var(--warning)' : 'var(--danger)' }}>
              %{data.on_time_pct}
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 4 }}>
              {data.on_time_count} / {data.total_orders}
              {data.avg_late_days && <> · Ort. {data.avg_late_days} gün gecikme</>}
            </div>
          </div>
          <div style={S.miniCard}>
            <div style={S.miniLabel}>Fiyat İstikrarı</div>
            <div style={{ ...S.miniValue, color: data.price_stability_score >= 80 ? 'var(--success)' : data.price_stability_score >= 60 ? 'var(--warning)' : 'var(--danger)' }}>
              {data.price_stability_score}
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 4 }}>
              %{data.price_variance_pct} sapma · {data.tracked_stock_count} ürün
            </div>
          </div>
        </div>
      </div>

      {/* Açıklama */}
      <div style={{ padding: 12, background: 'var(--bg-card-hover)', borderRadius: 'var(--r-md)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        <strong>📊 Skor nasıl hesaplanır?</strong><br />
        <strong>Zamanında teslim:</strong> İlgili sipariş varsa, planlanan teslim tarihinden önce gelen faturalar (%60 ağırlık).<br />
        <strong>Fiyat istikrarı:</strong> Aynı ürünleri farklı tarihlerde aldığında fiyat varyansı (%40 ağırlık). %0 sapma = 100 puan, %50 sapma = 50 puan.<br />
        <strong>Rating:</strong> A (≥85), B (≥70), C (≥55), D (&lt;55).
      </div>
    </div>
  );
}

const S = {
  bigCard: {
    padding: 16,
    background: 'var(--bg-card)',
    border: '2px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
  },
  miniCard: {
    padding: 12,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
  },
  miniLabel: {
    fontSize: '0.65rem',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    fontWeight: 700,
    letterSpacing: '0.04em',
  },
  miniValue: {
    fontSize: 'var(--text-2xl)',
    fontWeight: 700,
    color: 'var(--text-primary)',
    fontFamily: 'JetBrains Mono, monospace',
    marginTop: 4,
  },
};
